import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosClient from '../../../axios-client';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, DeleteIcon, EditIcon, WarningIcon } from '@chakra-ui/icons';
import FloatingPlusButton from '../../../components/FloatingPlusButton';
import { useTranslation } from 'react-i18next';
import { useStateContext } from '../../../context/ContextProvider';
import ReactSelect from 'react-select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { MdDragHandle, MdExpandMore, MdChevronRight, MdDriveFileRenameOutline } from 'react-icons/md';
import { FaLayerGroup, FaTrash } from 'react-icons/fa';
import {
    IconButton, Flex, Box, Button, Stack, InputGroup, InputLeftElement,
    Input, FormControl, FormLabel, useToast, useColorModeValue, useDisclosure,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Alert, AlertIcon,
    Text, Badge, Skeleton, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    Textarea, Collapse, Tooltip, Icon,
} from '@chakra-ui/react';

// ─── Drag handle icon ────────────────────────────────────────────────────────
const DragHandleIcon = () => (
    <Icon as={MdDragHandle} w={5} h={5} color="gray.400" cursor="grab"
        _active={{ cursor: 'grabbing' }} transition="color 0.2s" _hover={{ color: 'purple.400' }} />
);

// ─── Tutorial row ─────────────────────────────────────────────────────────────
const TutorielRow = ({ tuto, index, onEdit, onDelete, t, rowBg, rowHoverBg, borderColor }) => (
    <Draggable draggableId={`tuto-${tuto.id}`} index={index}>
        {(provided, snapshot) => (
            <Flex
                ref={provided.innerRef} {...provided.draggableProps}
                align="center" gap={3} px={4} py={3}
                bg={snapshot.isDragging ? rowHoverBg : rowBg}
                borderRadius="xl" borderWidth="1px"
                borderColor={snapshot.isDragging ? 'purple.300' : borderColor}
                mb={2} shadow={snapshot.isDragging ? 'lg' : 'sm'}
                transform={snapshot.isDragging ? 'scale(1.02)' : 'scale(1)'}
                opacity={snapshot.isDragging ? 0.95 : 1}
                transition="all 0.15s ease" _hover={{ bg: rowHoverBg, shadow: 'sm' }}
            >
                <Box {...provided.dragHandleProps} display="flex" alignItems="center" flexShrink={0}>
                    <DragHandleIcon />
                </Box>
                <Text flex="1" fontWeight="medium" fontSize="sm" isTruncated>{tuto.title}</Text>
                <Text as="a" href={tuto.youtube_link} target="_blank" rel="noopener noreferrer"
                    color="purple.500" fontSize="xs" maxW="200px" isTruncated
                    display={{ base: 'none', md: 'block' }} _hover={{ textDecoration: 'underline' }}>
                    {tuto.youtube_link}
                </Text>
                <Text fontSize="xs" color="gray.500" flexShrink={0} display={{ base: 'none', lg: 'block' }}>
                    {new Date(tuto.date_creation).toLocaleDateString('fr-FR')}
                </Text>
                <Flex gap={2} flexShrink={0}>
                    <Tooltip label={t('Modifier')} hasArrow>
                        <IconButton size="xs" icon={<EditIcon />} onClick={() => onEdit(tuto)}
                            variant="outline" colorScheme="whatsapp" borderRadius="lg" aria-label={t('Modifier')} />
                    </Tooltip>
                    <Tooltip label={t('Supprimer')} hasArrow>
                        <IconButton size="xs" icon={<DeleteIcon />} onClick={() => onDelete(tuto.id)}
                            variant="outline" colorScheme="red" borderRadius="lg" aria-label={t('Supprimer')} />
                    </Tooltip>
                </Flex>
            </Flex>
        )}
    </Draggable>
);

// ─── Category block ───────────────────────────────────────────────────────────
const CategoryBlock = ({
    category, index, onEdit, onDelete, t,
    cardBg, cardInnerBg, rowBg, rowHoverBg, borderColor,
    expandedCategories, toggleCategory, onRenameCategory, onDeleteCategory,
}) => {
    const isExpanded = expandedCategories[category.nom] !== false;
    return (
        <Draggable draggableId={`cat-${category.nom}`} index={index}>
            {(provided, snapshot) => (
                <Box ref={provided.innerRef} {...provided.draggableProps}
                    bg={snapshot.isDragging ? cardInnerBg : cardBg}
                    borderRadius="2xl" borderWidth="1px"
                    borderColor={snapshot.isDragging ? 'purple.400' : borderColor}
                    shadow={snapshot.isDragging ? 'xl' : 'sm'}
                    transform={snapshot.isDragging ? 'scale(1.01)' : 'scale(1)'}
                    opacity={snapshot.isDragging ? 0.97 : 1}
                    transition="all 0.15s ease" overflow="hidden" mb={3}
                >
                    <Flex align="center" gap={3} px={5} py={4} cursor="pointer"
                        onClick={() => toggleCategory(category.nom)}
                        _hover={{ bg: rowHoverBg }} transition="background 0.15s"
                    >
                        <Box {...provided.dragHandleProps} display="flex" alignItems="center" flexShrink={0}
                            onClick={e => e.stopPropagation()}>
                            <DragHandleIcon />
                        </Box>
                        <Icon as={FaLayerGroup} color="purple.400" w={4} h={4} flexShrink={0} />
                        <Text flex="1" fontWeight="bold" fontSize="md">{category.nom}</Text>
                        <Badge colorScheme="purple" borderRadius="full" px={2}>{category.tutoriels.length}</Badge>
                        <Flex gap={1} flexShrink={0} onClick={e => e.stopPropagation()}>
                            <Tooltip label={t('Renommer la catégorie')} hasArrow>
                                <IconButton size="xs" icon={<Icon as={MdDriveFileRenameOutline} w={4} h={4} />}
                                    onClick={() => onRenameCategory(category.nom)}
                                    variant="ghost" colorScheme="blue" borderRadius="lg"
                                    aria-label={t('Renommer la catégorie')} />
                            </Tooltip>
                            <Tooltip label={t('Supprimer la catégorie')} hasArrow>
                                <IconButton size="xs" icon={<Icon as={FaTrash} w={3} h={3} />}
                                    onClick={() => onDeleteCategory(category.nom, category.tutoriels.length)}
                                    variant="ghost" colorScheme="red" borderRadius="lg"
                                    aria-label={t('Supprimer la catégorie')} />
                            </Tooltip>
                        </Flex>
                        <Icon as={isExpanded ? MdExpandMore : MdChevronRight} w={5} h={5}
                            color="gray.500" transition="transform 0.2s" />
                    </Flex>
                    <Collapse in={isExpanded} animateOpacity>
                        <Box px={4} pb={4}>
                            <Droppable droppableId={`cat-${category.nom}`} type="TUTORIEL">
                                {(dropProvided, dropSnapshot) => (
                                    <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}
                                        minH="60px"
                                        bg={dropSnapshot.isDraggingOver ? 'purple.50' : 'transparent'}
                                        borderRadius="xl" p={dropSnapshot.isDraggingOver ? 2 : 0}
                                        transition="all 0.15s ease"
                                    >
                                        {category.tutoriels.length === 0 ? (
                                            <Flex align="center" justify="center" h="60px" borderRadius="xl"
                                                borderWidth="1px" borderColor={borderColor} borderStyle="dashed">
                                                <Text fontSize="sm" color="gray.400">
                                                    {t('Aucun tutoriel dans cette catégorie')}
                                                </Text>
                                            </Flex>
                                        ) : (
                                            category.tutoriels.map((tuto, tIndex) => (
                                                <TutorielRow key={tuto.id} tuto={tuto} index={tIndex}
                                                    onEdit={onEdit} onDelete={onDelete} t={t}
                                                    rowBg={cardInnerBg} rowHoverBg={rowHoverBg} borderColor={borderColor} />
                                            ))
                                        )}
                                        {dropProvided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </Box>
                    </Collapse>
                </Box>
            )}
        </Draggable>
    );
};

// ─── Helper: apply a tutorial update to categories state ─────────────────────
const applyTutoUpdate = (prev, updatedTuto, oldCatName) => {
    const newCatKey = (updatedTuto.categorie || '').trim().toLowerCase();
    const oldCatKey = (oldCatName || '').trim().toLowerCase();

    // Remove the tuto from wherever it was
    let newCats = prev.map(cat => ({
        ...cat,
        tutoriels: cat.tutoriels.filter(t => t.id !== updatedTuto.id),
    }));

    // Add to its new category
    const targetIdx = newCats.findIndex(c => c.nom.trim().toLowerCase() === newCatKey);
    if (targetIdx >= 0) {
        // Add at end of existing category
        newCats[targetIdx] = {
            ...newCats[targetIdx],
            tutoriels: [...newCats[targetIdx].tutoriels, updatedTuto],
        };
    } else if (newCatKey) {
        // Brand new category
        newCats = [...newCats, { nom: updatedTuto.categorie.trim(), ordre: 9999, tutoriels: [updatedTuto] }];
    }
    return newCats;
};

// ─── Main component ───────────────────────────────────────────────────────────
export const GestTutorielsAdmin = () => {
    const { t } = useTranslation();
    const [loadingData, setLoadingData] = useState(true);
    const navigate = useNavigate();
    const toast = useToast();

    // Tutorial modal
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isEditMode, setIsEditMode] = useState(false);

    // Tutorial delete
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [selectedTutoId, setSelectedTutoId] = useState(null);
    const cancelRef = useRef();

    // Category rename modal
    const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
    const [renameCatOldNom, setRenameCatOldNom] = useState('');
    const [renameCatNewNom, setRenameCatNewNom] = useState('');
    const [renameCatError, setRenameCatError] = useState('');
    const [renameCatLoading, setRenameCatLoading] = useState(false);

    // Category delete
    const [isDeleteCatOpen, setIsDeleteCatOpen] = useState(false);
    const [deleteCatNom, setDeleteCatNom] = useState('');
    const [deleteCatCount, setDeleteCatCount] = useState(0);
    const cancelCatRef = useRef();

    // Search & filter
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategorie, setFilterCategorie] = useState(null);

    // Tutorial form
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [youtubeLink, setYoutubeLink] = useState('');
    const [categorie, setCategorie] = useState('');
    const [formError, setFormError] = useState('');
    const [selectedTuto, setSelectedTuto] = useState(null);

    // Categories state (source of truth for DnD)
    const [categories, setCategories] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});

    // Colors
    const cardBg = useColorModeValue('white', 'gray.800');
    const cardInnerBg = useColorModeValue('gray.50', 'gray.700');
    const rowBg = useColorModeValue('white', 'gray.700');
    const rowHoverBg = useColorModeValue('gray.50', 'gray.600');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const headerBg = useColorModeValue('white', 'gray.800');

    const validateYoutubeLink = (url) => /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/.test(url);

    // ── Build structured data (initial load only) ───────────────────────────
    const buildCategories = useCallback((orderedCats, allTutoriels) => {
        const catMap = {};
        orderedCats.forEach(({ nom, ordre }) => {
            catMap[nom.trim().toLowerCase()] = { nom: nom.trim(), ordre, tutoriels: [] };
        });
        const unknownCats = {};
        allTutoriels.forEach(tuto => {
            const rawCat = (tuto.categorie || '').trim();
            const key = rawCat.toLowerCase() || '__uncategorized__';
            const displayName = rawCat || t('Sans catégorie');
            if (catMap[key]) {
                catMap[key].tutoriels.push(tuto);
            } else {
                if (!unknownCats[key]) unknownCats[key] = { nom: displayName, ordre: 9999, tutoriels: [] };
                unknownCats[key].tutoriels.push(tuto);
            }
        });
        const sortTutos = (arr) => [...arr].sort((a, b) => a.ordre - b.ordre || new Date(a.date_creation) - new Date(b.date_creation));
        return [
            ...Object.values(catMap).map(c => ({ ...c, tutoriels: sortTutos(c.tutoriels) })),
            ...Object.values(unknownCats).map(c => ({ ...c, tutoriels: sortTutos(c.tutoriels) })),
        ].sort((a, b) => a.ordre - b.ordre);
    }, [t]);

    // ── Initial fetch (only once) ───────────────────────────────────────────
    const fetchAll = useCallback(() => {
        setLoadingData(true);
        Promise.all([
            axiosClient.get('/tutoriels/get_categories/'),
            axiosClient.get('/tutoriels/getAllTutoriels/?page_size=1000&page=1&search_term='),
        ])
            .then(([catRes, tutoRes]) => {
                setCategories(buildCategories(catRes.data || [], tutoRes.data.results || []));
                setLoadingData(false);
            })
            .catch(() => setLoadingData(false));
    }, [buildCategories]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Local filter (no reload) ────────────────────────────────────────────
    const filteredCategories = categories
        .filter(cat => !filterCategorie || cat.nom.toLowerCase() === filterCategorie.value.toLowerCase())
        .map(cat => ({
            ...cat,
            tutoriels: cat.tutoriels.filter(tuto =>
                !searchTerm ||
                tuto.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (tuto.description || '').toLowerCase().includes(searchTerm.toLowerCase())
            ),
        }))
        .filter(cat => !searchTerm || cat.tutoriels.length > 0);

    const categorieOptions = categories.map(cat => ({ value: cat.nom, label: cat.nom }));

    const toggleCategory = (nom) =>
        setExpandedCategories(prev => ({ ...prev, [nom]: !(prev[nom] !== false) }));

    // ── DnD (already optimistic, no reload) ────────────────────────────────
    const onDragEnd = (result) => {
        const { source, destination, type } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const prevCategories = categories;

        if (type === 'CATEGORY') {
            const reordered = Array.from(categories);
            const [moved] = reordered.splice(source.index, 1);
            reordered.splice(destination.index, 0, moved);
            const updated = reordered.map((cat, idx) => ({ ...cat, ordre: idx }));
            setCategories(updated);
            setIsSaving(true);
            axiosClient.post('/tutoriels/reorder_categories/', updated.map(c => ({ nom: c.nom, ordre: c.ordre })))
                .then(() => toast({ description: t('Ordre des catégories sauvegardé'), status: 'success', duration: 2000, isClosable: true, variant: 'left-accent', position: 'bottom-right' }))
                .catch(() => { setCategories(prevCategories); toast({ description: t('Erreur — ordre restauré'), status: 'error', duration: 3000, isClosable: true, variant: 'left-accent', position: 'bottom-right' }); })
                .finally(() => setIsSaving(false));

        } else if (type === 'TUTORIEL') {
            const catIndex = categories.findIndex(c => `cat-${c.nom}` === destination.droppableId);
            if (catIndex === -1) return;
            const tutos = Array.from(categories[catIndex].tutoriels);
            const [movedTuto] = tutos.splice(source.index, 1);
            tutos.splice(destination.index, 0, movedTuto);
            const updatedTutos = tutos.map((t, idx) => ({ ...t, ordre: idx }));
            setCategories(prev => prev.map((cat, idx) => idx === catIndex ? { ...cat, tutoriels: updatedTutos } : cat));
            setIsSaving(true);
            axiosClient.post('/tutoriels/reorder_tutoriels/', updatedTutos.map(tuto => ({ id: tuto.id, ordre: tuto.ordre })))
                .then(() => toast({ description: t('Ordre des tutoriels sauvegardé'), status: 'success', duration: 2000, isClosable: true, variant: 'left-accent', position: 'bottom-right' }))
                .catch(() => { setCategories(prevCategories); toast({ description: t('Erreur — ordre restauré'), status: 'error', duration: 3000, isClosable: true, variant: 'left-accent', position: 'bottom-right' }); })
                .finally(() => setIsSaving(false));
        }
    };

    // ── Tutorial CRUD — all optimistic, no fetchAll() ──────────────────────

    const handleOpenAddModal = () => {
        setIsEditMode(false); setTitle(''); setDescription('');
        setYoutubeLink(''); setCategorie(''); setFormError(''); onOpen();
    };

    const handleOpenEditModal = (tuto) => {
        setIsEditMode(true); setSelectedTuto(tuto); setTitle(tuto.title);
        setDescription(tuto.description || ''); setYoutubeLink(tuto.youtube_link);
        setCategorie(tuto.categorie || ''); setFormError(''); onOpen();
    };

    const handleSave = () => {
        if (!title.trim()) { setFormError(t('Veuillez entrer un titre')); return; }
        if (!youtubeLink.trim() || !validateYoutubeLink(youtubeLink)) {
            setFormError(t('Veuillez entrer un lien YouTube valide')); return;
        }
        const payload = {
            title: title.trim(),
            description: description.trim() || null,
            youtube_link: youtubeLink.trim(),
            categorie: categorie ? categorie.trim() : null,
        };

        if (isEditMode && selectedTuto) {
            // EDIT — optimistic update on success
            axiosClient.patch(`/tutoriels/${selectedTuto.id}/`, payload)
                .then(res => {
                    const updatedTuto = res.data;
                    setCategories(prev => applyTutoUpdate(prev, updatedTuto, selectedTuto.categorie));
                    toast({ description: t('Tutoriel modifié avec succès'), status: 'success', duration: 2000, isClosable: true, variant: 'left-accent', position: 'bottom-right' });
                    onClose();
                })
                .catch(err => setFormError(err.response?.data?.detail || t('Une erreur est survenue')));
        } else {
            // ADD — insert into local state on success
            axiosClient.post('/tutoriels/', payload)
                .then(res => {
                    const newTuto = res.data;
                    const catKey = (newTuto.categorie || '').trim().toLowerCase();
                    setCategories(prev => {
                        const idx = prev.findIndex(c => c.nom.trim().toLowerCase() === catKey);
                        if (idx >= 0) {
                            return prev.map((cat, i) =>
                                i === idx ? { ...cat, tutoriels: [...cat.tutoriels, newTuto] } : cat
                            );
                        } else if (catKey) {
                            return [...prev, { nom: newTuto.categorie.trim(), ordre: 9999, tutoriels: [newTuto] }];
                        }
                        return prev;
                    });
                    toast({ description: t('Tutoriel ajouté avec succès'), status: 'success', duration: 2000, isClosable: true, variant: 'left-accent', position: 'bottom-right' });
                    onClose();
                })
                .catch(err => setFormError(err.response?.data?.detail || t('Une erreur est survenue')));
        }
    };

    const handleDeleteRequest = (id) => { setSelectedTutoId(id); setIsAlertOpen(true); };

    const handleDelete = () => {
        if (!selectedTutoId) return;
        // Optimistic: remove immediately
        const prevCategories = categories;
        setCategories(prev => prev.map(cat => ({
            ...cat,
            tutoriels: cat.tutoriels.filter(t => t.id !== selectedTutoId),
        })));
        setIsAlertOpen(false);

        axiosClient.delete(`/tutoriels/${selectedTutoId}/`)
            .then(() => toast({ description: t('Tutoriel supprimé avec succès'), status: 'warning', duration: 2000, isClosable: true, position: 'bottom-right', variant: 'left-accent' }))
            .catch(() => {
                setCategories(prevCategories);
                toast({ description: t('Erreur lors de la suppression'), status: 'error', duration: 2000, isClosable: true, position: 'bottom-right', variant: 'left-accent' });
            });
    };

    // ── Category CRUD — optimistic, no fetchAll() ───────────────────────────

    const handleOpenRenameCategory = (nom) => {
        setRenameCatOldNom(nom); setRenameCatNewNom(nom); setRenameCatError(''); onRenameOpen();
    };

    const handleRenameCategory = () => {
        if (!renameCatNewNom.trim()) { setRenameCatError(t('Veuillez entrer un nouveau nom')); return; }
        setRenameCatLoading(true);
        axiosClient.post('/tutoriels/rename_category/', { old_nom: renameCatOldNom, new_nom: renameCatNewNom.trim() })
            .then(() => {
                // Update local state — rename the category
                setCategories(prev => prev.map(cat =>
                    cat.nom.toLowerCase() === renameCatOldNom.toLowerCase()
                        ? { ...cat, nom: renameCatNewNom.trim(), tutoriels: cat.tutoriels.map(t => ({ ...t, categorie: renameCatNewNom.trim() })) }
                        : cat
                ));
                // Update filter if it was pointing to old name
                if (filterCategorie?.value?.toLowerCase() === renameCatOldNom.toLowerCase()) {
                    setFilterCategorie({ value: renameCatNewNom.trim(), label: renameCatNewNom.trim() });
                }
                toast({ description: t('Catégorie renommée avec succès'), status: 'success', duration: 2000, isClosable: true, variant: 'left-accent', position: 'bottom-right' });
                onRenameClose();
            })
            .catch(err => {
                setRenameCatError(err.response?.data?.error || t('Une erreur est survenue'));
            })
            .finally(() => setRenameCatLoading(false));
    };

    const handleOpenDeleteCategory = (nom, count) => {
        setDeleteCatNom(nom); setDeleteCatCount(count); setIsDeleteCatOpen(true);
    };

    const handleDeleteCategory = () => {
        // Optimistic: remove immediately
        const prevCategories = categories;
        setCategories(prev => prev.filter(cat => cat.nom.toLowerCase() !== deleteCatNom.toLowerCase()));
        setIsDeleteCatOpen(false);

        axiosClient.post('/tutoriels/delete_category/', { nom: deleteCatNom })
            .then(() => toast({
                description: t(`Catégorie supprimée avec ses vidéos`),
                status: 'warning', duration: 3000, isClosable: true, variant: 'left-accent', position: 'bottom-right',
            }))
            .catch(() => {
                setCategories(prevCategories);
                toast({ description: t('Erreur lors de la suppression'), status: 'error', duration: 2000, isClosable: true, variant: 'left-accent', position: 'bottom-right' });
            });
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box position="relative" mb={5}>
            <FloatingPlusButton tooltip={t('Ajouter un tutoriel')} onClick={handleOpenAddModal} />

            {/* Breadcrumbs */}
            <Flex>
                <Box w="100%" display="flex" justifyContent="space-between"
                    flexDirection={{ base: 'column', md: 'row' }} mt={2} mb={{ base: 0, md: 5 }}>
                    <Breadcrumb fontStyle="normal" fontSize={{ base: '15px', sm: '16px', lg: '17px' }} color="gray.400">
                        <BreadcrumbItem>
                            <BreadcrumbLink onClick={() => navigate('/')}>{t('Accueil')}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>{t('Tutoriels')}</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Box>
            </Flex>

            {/* Header card */}
            <Flex bg={headerBg} borderRadius="2xl" shadow="sm" px={5} py={4} mb={4}
                align="center" justify="space-between" gap={4}
                direction={{ base: 'column', md: 'row' }} borderWidth="1px" borderColor={borderColor}
            >
                <Flex align="center" gap={3}>
                    <Flex w="36px" h="36px" bg="purple.500" borderRadius="lg" align="center" justify="center" flexShrink={0}>
                        <Icon as={MdDragHandle} color="white" w={5} h={5} />
                    </Flex>
                    <Box>
                        <Text fontWeight="bold" fontSize="md">{t('Réorganisation par glisser-déposer')}</Text>
                        <Text fontSize="xs" color="gray.500">
                            {t("Glissez les catégories et tutoriels • Renommez ou supprimez via les icônes")}
                        </Text>
                    </Box>
                </Flex>
                <Flex gap={3} w={{ base: '100%', md: 'auto' }} direction={{ base: 'column', sm: 'row' }}>
                    <Box w={{ base: '100%', md: '220px' }}>
                        <ReactSelect
                            placeholder={t('Filtrer par catégorie')} isClearable
                            options={categorieOptions} value={filterCategorie}
                            onChange={s => setFilterCategorie(s)}
                            className="react-select-container" classNamePrefix="react-select"
                        />
                    </Box>
                    <InputGroup size="md" w={{ base: '100%', md: '240px' }}>
                        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.300" /></InputLeftElement>
                        <Input borderRadius="xl" placeholder={t('Recherche...')}
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </InputGroup>
                </Flex>
            </Flex>

            {/* Saving indicator (DnD only, brief) */}
            {isSaving && (
                <Flex align="center" gap={2} mb={3} px={4} py={2} bg="purple.50"
                    borderRadius="xl" borderWidth="1px" borderColor="purple.200">
                    <Box w="8px" h="8px" bg="purple.500" borderRadius="full" />
                    <Text fontSize="sm" color="purple.600">{t('Sauvegarde en cours...')}</Text>
                </Flex>
            )}

            {/* Content — skeleton only on initial load */}
            {loadingData ? (
                <Stack spacing={3}>
                    {[1, 2, 3].map(i => (
                        <Box key={i} bg={cardBg} borderRadius="2xl" p={5} shadow="sm">
                            <Skeleton height="20px" width="40%" mb={3} />
                            <Skeleton height="16px" mb={2} />
                            <Skeleton height="16px" width="80%" />
                        </Box>
                    ))}
                </Stack>
            ) : filteredCategories.length === 0 ? (
                <Flex direction="column" align="center" justify="center" p={10}
                    bg={cardBg} borderRadius="2xl" shadow="sm">
                    <Text fontSize="lg" color="gray.500" mb={2}>{t('Aucune catégorie trouvée')}</Text>
                    <Text fontSize="sm" color="gray.400">{t('Ajoutez un tutoriel pour commencer.')}</Text>
                </Flex>
            ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="categories" type="CATEGORY">
                        {(provided) => (
                            <Box ref={provided.innerRef} {...provided.droppableProps}>
                                {filteredCategories.map((cat, catIndex) => (
                                    <CategoryBlock
                                        key={cat.nom} category={cat} index={catIndex}
                                        onEdit={handleOpenEditModal} onDelete={handleDeleteRequest} t={t}
                                        cardBg={cardBg} cardInnerBg={cardInnerBg}
                                        rowBg={rowBg} rowHoverBg={rowHoverBg} borderColor={borderColor}
                                        expandedCategories={expandedCategories} toggleCategory={toggleCategory}
                                        onRenameCategory={handleOpenRenameCategory}
                                        onDeleteCategory={handleOpenDeleteCategory}
                                    />
                                ))}
                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            {/* ── Tutorial Create/Edit Modal ─────────────────────────────── */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent borderRadius="2xl">
                    <ModalHeader fontSize="2xl" fontWeight="bold">
                        {isEditMode ? t('Modifier le tutoriel') : t('Ajouter un tutoriel')}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {formError && (
                            <Alert mb={4} fontSize="sm" variant="left-accent" status="error" rounded="md">
                                <AlertIcon />{formError}
                            </Alert>
                        )}
                        <Stack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel fontWeight="semibold">{t('Titre du tutoriel')}</FormLabel>
                                <Input borderRadius="xl" value={title} onChange={e => setTitle(e.target.value)}
                                    placeholder={t('Ex: Comment importer vos commandes via Excel')} />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontWeight="semibold">{t('Catégorie')}</FormLabel>
                                <Input borderRadius="xl" value={categorie} onChange={e => setCategorie(e.target.value)}
                                    placeholder={t('Ex: Commandes, Stock, Finance...')} />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontWeight="semibold">{t('Description du tutoriel')}</FormLabel>
                                <Textarea borderRadius="xl" value={description} onChange={e => setDescription(e.target.value)}
                                    placeholder={t('Ex: Ce tutoriel explique pas à pas comment formater votre Excel...')} rows={4} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontWeight="semibold">{t('Lien YouTube')}</FormLabel>
                                <Input borderRadius="xl" value={youtubeLink} onChange={e => setYoutubeLink(e.target.value)}
                                    placeholder={t('Ex: https://www.youtube.com/watch?v=...')} />
                            </FormControl>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose} borderRadius="xl">{t('Annuler')}</Button>
                        <Button colorScheme="purple" onClick={handleSave} borderRadius="xl" shadow="md">
                            {isEditMode ? t('Modifier') : t('Ajouter')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Tutorial Delete Alert ──────────────────────────────────── */}
            <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef} onClose={() => setIsAlertOpen(false)}>
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="2xl">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">{t('Supprimer le tutoriel')}</AlertDialogHeader>
                        <AlertDialogBody>{t('Êtes-vous sûr de vouloir supprimer ce tutoriel ?')}</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)} borderRadius="xl">{t('Annuler')}</Button>
                            <Button colorScheme="red" onClick={handleDelete} ml={3} borderRadius="xl" shadow="md">{t('Supprimer')}</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* ── Category Rename Modal ──────────────────────────────────── */}
            <Modal isOpen={isRenameOpen} onClose={onRenameClose} size="md">
                <ModalOverlay />
                <ModalContent borderRadius="2xl">
                    <ModalHeader fontSize="xl" fontWeight="bold">
                        <Flex align="center" gap={2}>
                            <Icon as={MdDriveFileRenameOutline} color="blue.400" />
                            {t('Renommer la catégorie')}
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {renameCatError && (
                            <Alert mb={4} fontSize="sm" variant="left-accent" status="error" rounded="md">
                                <AlertIcon />{renameCatError}
                            </Alert>
                        )}
                        <FormControl isRequired>
                            <FormLabel fontWeight="semibold">{t('Nouveau nom de la catégorie')}</FormLabel>
                            <Input borderRadius="xl" value={renameCatNewNom} autoFocus
                                onChange={e => { setRenameCatNewNom(e.target.value); setRenameCatError(''); }}
                                placeholder={t('Ex: Commandes, Gestion Stock...')} />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                {t('Nom actuel')}: <b>{renameCatOldNom}</b>
                            </Text>
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onRenameClose} borderRadius="xl">{t('Annuler')}</Button>
                        <Button colorScheme="blue" onClick={handleRenameCategory}
                            borderRadius="xl" shadow="md" isLoading={renameCatLoading}>
                            {t('Renommer')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Category Delete Alert ──────────────────────────────────── */}
            <AlertDialog isOpen={isDeleteCatOpen} leastDestructiveRef={cancelCatRef} onClose={() => setIsDeleteCatOpen(false)}>
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="2xl">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            <Flex align="center" gap={2}>
                                <Icon as={WarningIcon} color="red.500" />
                                {t('Supprimer la catégorie')}
                            </Flex>
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            <Stack spacing={3}>
                                <Text>
                                    {t('Êtes-vous sûr de vouloir supprimer la catégorie')} <b>"{deleteCatNom}"</b> ?
                                </Text>
                                <Alert status="error" borderRadius="xl" variant="left-accent">
                                    <AlertIcon />
                                    <Box>
                                        <Text fontWeight="bold" fontSize="sm">{t('Action irréversible')}</Text>
                                        <Text fontSize="sm">
                                            {deleteCatCount > 0
                                                ? `${deleteCatCount} ${t('tutoriel(s) seront définitivement supprimé(s).')}`
                                                : t('La catégorie sera supprimée.')}
                                        </Text>
                                    </Box>
                                </Alert>
                            </Stack>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelCatRef} onClick={() => setIsDeleteCatOpen(false)} borderRadius="xl">{t('Annuler')}</Button>
                            <Button colorScheme="red" onClick={handleDeleteCategory} ml={3} borderRadius="xl" shadow="md">
                                {t('Supprimer la catégorie')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default GestTutorielsAdmin;
